import { type KeyboardEvent, useRef } from 'react';

export interface RovingTabsOptions {
    count: number;
    /** -1 when nothing is selected; the first enabled tab then takes the tab stop. */
    selectedIndex: number;
    isDisabled: (index: number) => boolean;
    /**
     * Whether moving focus onto a tab also selects it. True for tabs that switch content in
     * place, so arrows behave like the WAI-ARIA "automatic activation" pattern; false for
     * link tabs, where selecting means navigating and Enter does that.
     */
    activatesOnFocus: (index: number) => boolean;
}

export interface RovingTabs {
    setRef: (index: number) => (element: HTMLElement | null) => void;
    tabIndexFor: (index: number) => 0 | -1;
    onKeyDown: (event: KeyboardEvent<HTMLElement>, index: number) => void;
}

/**
 * One tab stop for a whole tablist, moved with the arrow keys, Home and End.
 *
 * Activation goes through the element's own `click()`, so a keyboard selection reaches the
 * caller as the same click event a pointer selection does.
 */
export function useRovingTabs(options: RovingTabsOptions): RovingTabs {
    const { count, selectedIndex, isDisabled, activatesOnFocus } = options;
    const elements = useRef<Array<HTMLElement | null>>([]);

    const firstEnabled = (): number => {
        for (let index = 0; index < count; index += 1) {
            if (!isDisabled(index)) return index;
        }
        return -1;
    };

    const stopIndex =
        selectedIndex >= 0 && !isDisabled(selectedIndex) ? selectedIndex : firstEnabled();

    const step = (from: number, direction: 1 | -1): number => {
        for (let offset = 1; offset <= count; offset += 1) {
            const candidate = (from + direction * offset + count) % count;
            if (!isDisabled(candidate)) return candidate;
        }
        return from;
    };

    const lastEnabled = (): number => {
        for (let index = count - 1; index >= 0; index -= 1) {
            if (!isDisabled(index)) return index;
        }
        return -1;
    };

    const onKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
        let target = -1;
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
                target = step(index, 1);
                break;
            case 'ArrowLeft':
            case 'ArrowUp':
                target = step(index, -1);
                break;
            case 'Home':
                target = firstEnabled();
                break;
            case 'End':
                target = lastEnabled();
                break;
            default:
                return;
        }
        event.preventDefault();
        const element = elements.current[target];
        if (target < 0 || target === index || !element) return;
        element.focus();
        if (activatesOnFocus(target)) {
            element.click();
        }
    };

    return {
        setRef: (index) => (element) => {
            elements.current[index] = element;
        },
        tabIndexFor: (index) => (index === stopIndex ? 0 : -1),
        onKeyDown,
    };
}
